// Feature Flag Utility System
// This file provides the core functionality for feature flag evaluation

import { FEATURES, FEATURE_ROLLOUT_CONFIG } from '../features.js';
import { TOG_PLUTOO_CONFIG } from '../clients/tog-plutoo.js';
import { DEMO_PLUTOO_CONFIG } from '../clients/demo-plutoo.js';
import React from 'react';
import { doc, getDoc, getFirestore, onSnapshot } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getFirebaseDb } from '../config/firebase';

// ─── Plutoo Hub Firebase connection (read-only, for central feature flags) ────
// The Hub stores feature overrides per company at companyFeatures/{companyName}
// All values must be provided via environment variables — no hardcoded fallbacks
const HUB_FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_HUB_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_HUB_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_HUB_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_HUB_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_HUB_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_HUB_FIREBASE_APP_ID,
};

const HUB_APP_NAME = 'plutoo-hub-readonly';

const getHubDb = () => {
  const existing = getApps().find((a) => a.name === HUB_APP_NAME);
  const hubApp = existing || initializeApp(HUB_FIREBASE_CONFIG, HUB_APP_NAME);
  return getFirestore(hubApp);
};

// Client configuration mapping
const CLIENT_CONFIGS = {
  'tog-plutoo': TOG_PLUTOO_CONFIG,
  'demo-plutoo': DEMO_PLUTOO_CONFIG,
};

/**
 * Get the current client configuration
 * @returns {Object} Client configuration object
 */
export const getCurrentClientConfig = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';
  return CLIENT_CONFIGS[companyName] || DEMO_PLUTOO_CONFIG;
};

/**
 * Get client configuration by name
 * @param {string} clientName - Name of the client
 * @returns {Object} Client configuration object
 */
export const getClientConfig = (clientName) => {
  return CLIENT_CONFIGS[clientName] || DEMO_PLUTOO_CONFIG;
};

/**
 * Simple hash function for consistent user-based rollouts
 * @param {string} str - String to hash
 * @returns {number} Hash value
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Get current user ID (from auth context or localStorage)
 * @returns {string} User ID or fallback
 */
const getCurrentUserId = () => {
  // In a real app, this would come from your auth context
  // For now, we'll use a fallback or localStorage
  return localStorage.getItem('userId') || 'anonymous';
};

// ─── Hub-managed feature flags (highest priority) ─────────────────────────────
let cachedHubFeatures = null;
let hubCacheTimestamp = null;
let hubLoadingPromise = null;

export const loadHubFeatureFlags = async () => {
  try {
    const now = Date.now();
    if (cachedHubFeatures !== null && hubCacheTimestamp && (now - hubCacheTimestamp) < CACHE_DURATION) {
      return cachedHubFeatures;
    }
    if (hubLoadingPromise) return hubLoadingPromise;

    hubLoadingPromise = (async () => {
      try {
        const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';
        const hubDb = getHubDb();
        const featuresRef = doc(hubDb, 'companyFeatures', companyName);
        const snap = await getDoc(featuresRef);
        const features = snap.exists() ? (snap.data().features || null) : null;
        cachedHubFeatures = features;
        hubCacheTimestamp = Date.now();
        return features;
      } catch (err) {
        // Hub unreachable - fall through to local settings
        cachedHubFeatures = null;
        hubCacheTimestamp = Date.now();
        return null;
      } finally {
        hubLoadingPromise = null;
      }
    })();
    return hubLoadingPromise;
  } catch {
    hubLoadingPromise = null;
    return null;
  }
};

export const getHubFeatureFlags = () => cachedHubFeatures;

export const clearHubFeatureFlagsCache = () => {
  cachedHubFeatures = null;
  hubCacheTimestamp = null;
  hubLoadingPromise = null;
};

// ─── Local Firestore feature toggles ──────────────────────────────────────────
/**
 * Get feature toggles from Firestore settings
 * This checks for global feature overrides stored in Firestore
 * Uses a cache to avoid blocking on every call
 */
let cachedFeatureToggles = null;
let cacheTimestamp = null;
const CACHE_DURATION = 60000; // 1 minute cache
let loadingPromise = null;

const isPermissionDeniedError = (error) => (
  error?.code === 'permission-denied' ||
  error?.code === 'PERMISSION_DENIED' ||
  error?.message?.toLowerCase().includes('missing or insufficient permissions')
);

/**
 * Load feature toggles from Firestore (async)
 * @returns {Promise<Object>} Object with feature names as keys and boolean values, or null if not available
 */
export const loadFeatureTogglesFromSettings = async () => {
  try {
    // Check cache first
    const now = Date.now();
    if (cachedFeatureToggles !== null && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      return cachedFeatureToggles;
    }

    // If already loading, return the existing promise
    if (loadingPromise) {
      return loadingPromise;
    }

    // Start loading
    loadingPromise = (async () => {
      try {
        const db = getFirebaseDb();
        const settingsRef = doc(db, 'settings', 'appSettings');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const features = settingsSnap.data().features || null;
          cachedFeatureToggles = features;
          cacheTimestamp = Date.now();
          return features;
        }
        
        cachedFeatureToggles = null;
        cacheTimestamp = Date.now();
        return null;
      } catch (error) {
        if (!isPermissionDeniedError(error)) {
          console.error('Error fetching feature toggles from Firestore:', error);
        }
        cachedFeatureToggles = null;
        cacheTimestamp = Date.now();
        return null;
      } finally {
        loadingPromise = null;
      }
    })();

    return loadingPromise;
  } catch (error) {
    console.error('Error in loadFeatureTogglesFromSettings:', error);
    loadingPromise = null;
    return null;
  }
};

/**
 * Get cached feature toggles (synchronous)
 * @returns {Object|null} Cached feature toggles or null
 */
export const getFeatureTogglesFromSettings = () => {
  return cachedFeatureToggles;
};

/**
 * Clear the feature toggles cache
 * Call this after updating features in Settings
 */
export const clearFeatureTogglesCache = () => {
  cachedFeatureToggles = null;
  cacheTimestamp = null;
  loadingPromise = null;
};

// Initialize both caches on module load
if (typeof window !== 'undefined') {
  // Load Hub feature flags first (highest priority)
  loadHubFeatureFlags().catch(() => {});

  // Load local Firestore feature toggles
  loadFeatureTogglesFromSettings().catch(() => {});

  // Refresh both caches periodically
  setInterval(() => {
    loadHubFeatureFlags().catch(() => {});
    loadFeatureTogglesFromSettings().catch(() => {});
  }, CACHE_DURATION);
}

/**
 * Check if a feature is enabled for the current client
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';
  const clientConfig = getCurrentClientConfig();
  
  // Check if feature exists
  if (!FEATURE_ROLLOUT_CONFIG[featureName]) {
    console.warn(`Feature "${featureName}" not found in configuration`);
    return false;
  }
  
  // FIRST: Check Hub-managed features (highest priority — set via Plutoo Hub Company Management)
  const hubToggles = getHubFeatureFlags();
  if (hubToggles && Object.prototype.hasOwnProperty.call(hubToggles, featureName)) {
    return hubToggles[featureName];
  }

  // SECOND: Check local Firestore settings (per-instance override via Settings page)
  // Use cached value for synchronous access
  const firestoreToggles = getFeatureTogglesFromSettings();
  if (firestoreToggles && firestoreToggles.hasOwnProperty(featureName)) {
    return firestoreToggles[featureName];
  }
  
  // SECOND: Check client-specific override
  if (clientConfig.features && clientConfig.features.hasOwnProperty(featureName)) {
    return clientConfig.features[featureName];
  }
  
  // THIRD: Check global rollout configuration
  const rolloutConfig = FEATURE_ROLLOUT_CONFIG[featureName];
  
  // Check if explicitly disabled for this client
  if (rolloutConfig[companyName] === false) {
    return false;
  }
  
  // Check if explicitly enabled for this client
  if (rolloutConfig[companyName] === true) {
    return true;
  }
  
  // Check rollout percentage
  if (rolloutConfig.rolloutPercentage > 0) {
    const userId = getCurrentUserId();
    const userHash = hashString(userId + featureName) % 100;
    return userHash < rolloutConfig.rolloutPercentage;
  }
  
  return false;
};

/**
 * React hook for using feature flags.
 * Subscribes to Hub Firestore for live updates — re-renders when flags change.
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} Whether the feature is enabled
 */
export const useFeatureFlag = (featureName) => {
  const [hubFlags, setHubFlags] = React.useState(() => getHubFeatureFlags());
  const [localFlags, setLocalFlags] = React.useState(() => getFeatureTogglesFromSettings());

  React.useEffect(() => {
    const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';

    // Load Hub flags immediately and subscribe for live changes
    loadHubFeatureFlags().then((flags) => {
      if (flags) setHubFlags(flags);
    }).catch(() => {});

    try {
      const hubDb = getHubDb();
      const unsubscribeHub = onSnapshot(
        doc(hubDb, 'companyFeatures', companyName),
        (snap) => {
          if (snap.exists()) {
            const features = snap.data().features || null;
            cachedHubFeatures = features;
            hubCacheTimestamp = Date.now();
            setHubFlags(features);
          }
        },
        () => {} // Silently fail if Hub unreachable
      );

      // Also subscribe to local Firestore settings for per-instance overrides
      const localRef = doc(getFirebaseDb(), 'settings', 'appSettings');
      const unsubscribeLocal = onSnapshot(
        localRef,
        (snap) => {
          if (snap.exists() && snap.data().features) {
            const features = snap.data().features;
            cachedFeatureToggles = features;
            cacheTimestamp = Date.now();
            setLocalFlags(features);
          }
        },
        () => {}
      );

      return () => {
        unsubscribeHub();
        unsubscribeLocal();
      };
    } catch {
      return undefined;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureName]);

  // Hub flags take priority over local flags
  if (hubFlags && Object.prototype.hasOwnProperty.call(hubFlags, featureName)) {
    return hubFlags[featureName];
  }
  if (localFlags && Object.prototype.hasOwnProperty.call(localFlags, featureName)) {
    return localFlags[featureName];
  }
  return isFeatureEnabled(featureName);
};

/**
 * Get all enabled features for the current client
 * @returns {Object} Object with feature names as keys and boolean values
 */
export const getAllEnabledFeatures = () => {
  const enabledFeatures = {};
  
  Object.keys(FEATURES).forEach(featureKey => {
    const featureName = FEATURES[featureKey];
    enabledFeatures[featureName] = isFeatureEnabled(featureName);
  });
  
  return enabledFeatures;
};

/**
 * Get feature information including description and status
 * @param {string} featureName - Name of the feature
 * @returns {Object} Feature information
 */
export const getFeatureInfo = (featureName) => {
  const rolloutConfig = FEATURE_ROLLOUT_CONFIG[featureName];
  
  if (!rolloutConfig) {
    return {
      exists: false,
      enabled: false,
      description: 'Feature not found',
      stable: false,
    };
  }
  
  return {
    exists: true,
    enabled: isFeatureEnabled(featureName),
    description: rolloutConfig.description || 'No description available',
    stable: rolloutConfig.stable || false,
    rolloutPercentage: rolloutConfig.rolloutPercentage || 0,
  };
};

/**
 * Utility function to conditionally render components based on feature flags
 * @param {string} featureName - Name of the feature
 * @param {React.Component} component - Component to render if feature is enabled
 * @param {React.Component} fallback - Component to render if feature is disabled
 * @returns {React.Component} Component to render
 */
export const FeatureGate = ({ feature, children, fallback = null }) => {
  const isEnabled = useFeatureFlag(feature);
  return isEnabled ? children : fallback;
};

/**
 * Higher-order component for feature flag protection
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} featureName - Name of the feature
 * @returns {React.Component} Wrapped component with feature flag protection
 */
export const withFeatureFlag = (WrappedComponent, featureName) => {
  return function FeatureFlaggedComponent(props) {
    const isEnabled = useFeatureFlag(featureName);
    
    if (!isEnabled) {
      return null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

/**
 * Debug function to log all feature flags for current client
 * (Only works in development mode)
 */
export const debugFeatureFlags = () => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'demo-plutoo';
  const allFeatures = getAllEnabledFeatures();
  
  console.group(`🚩 Feature Flags for ${companyName}`);
  console.log('Current client config:', getCurrentClientConfig());
  console.log('All features:', allFeatures);
  console.log('Enabled features:', Object.keys(allFeatures).filter(f => allFeatures[f]));
  console.log('Disabled features:', Object.keys(allFeatures).filter(f => !allFeatures[f]));
  console.groupEnd();
};

// Export the FEATURES constant for easy access
export { FEATURES }; 
